import React, { useState, useEffect } from 'react';
// import { logger } from "../utils/logger";
import { Box, Alert, Typography, Button, Card, CardContent, Stack, Chip, Divider, Link as MuiLink, IconButton } from '@mui/material';
import { Info as InfoIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Schedule as ScheduleIcon, Business as BusinessIcon, Email as EmailIcon, Phone as PhoneIcon, Celebration as CelebrationIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProviderAccess } from '../hooks/useProviderAccess';
import { logger } from "../utils/logger";
// Utility function to generate consistent localStorage key
const getDismissalKey = (agencyName: string | null): string => {
    return `provider_approval_dismissed_${agencyName || 'unknown'}`;
};

const ProviderAccessBanner: React.FC = () => {
    const { user } = useAuth();
    const { applicationStatus, submittedAt, reviewedAt, agencyName, rejectionReason, publishedEventsCount, loading, error, isProvider, isPendingProvider, isApprovedProvider, isRejectedProvider } = useProviderAccess();
    const [shouldShow, setShouldShow] = useState(true);
    // Dismissal / expiry only for the post-first-event celebration banner
    useEffect(() => {
        if (!isApprovedProvider || publishedEventsCount === 0) {
            setShouldShow(true);
            return;
        }
        if (!reviewedAt) {
            setShouldShow(false);
            return;
        }
        // Check if user has dismissed the banner
        const dismissedKey = getDismissalKey(agencyName);
        const dismissed = localStorage.getItem(dismissedKey);
        if (dismissed === 'true') {
            setShouldShow(false);
            return;
        }
        // Check if 7 days have passed since approval
        try {
            const approvalDate = new Date(reviewedAt);
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            // Check if the date is valid and if 7 days have passed
            if (!isNaN(approvalDate.getTime()) && approvalDate < sevenDaysAgo) {
                setShouldShow(false);
                return;
            }
        }
        catch (error) {
            // If there's an error parsing the date, show the banner
            logger.error('Error parsing approval date:', error, {});
        }
        setShouldShow(true);
    }, [isApprovedProvider, reviewedAt, agencyName, publishedEventsCount]);
    const handleDismiss = () => {
        const dismissedKey = getDismissalKey(agencyName);
        localStorage.setItem(dismissedKey, 'true');
        setShouldShow(false);
    };

    // Don't reserve blank height while loading — most users never get a banner, and the
    // empty band under the sticky header opens then closes (visible CLS flash). Prefer a
    // rare pop-in for providers over a gap for everyone.
    if (
        loading ||
        user?.role === 'coordinator' ||
        user?.role === 'training_manager' ||
        user?.role === 'admin' ||
        (applicationStatus === null && user?.role !== 'provider' && user?.role !== 'instructor')
    ) {
        return null;
    }
    if (!isProvider && applicationStatus === null) {
        return null;
    }
    // Celebration banner dismissal only applies after first published event
    if (isApprovedProvider && publishedEventsCount > 0 && !shouldShow) {
        return null;
    }
    if (isApprovedProvider && publishedEventsCount > 0 && !reviewedAt) {
        return null;
    }
    // Show error state
    if (error) {
        return (<Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Unable to check your provider application status. Please try refreshing the page.
        </Typography>
      </Alert>);
    }
    // Show pending status
    if (isPendingProvider) {
        return (<Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <ScheduleIcon color="warning" sx={{ fontSize: 40, mt: 1 }}/>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'warning.main' }}>
                Application Under Review
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your provider application for <strong>{agencyName}</strong> is currently under review. 
                You can explore the platform, but you won't be able to create training events until your application is approved.
              </Typography>
              
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Submitted:</strong> {submittedAt ? new Date(submittedAt).toLocaleDateString() : 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> Under review by our team
                </Typography>
              </Stack>

              <Divider sx={{ my: 2 }}/>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>What you can do while waiting:</strong>
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  • Browse existing training events and venues
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Update your profile and agency information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Contact support if you have questions
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>);
    }
    // Show approved status — zero published events: nudge to create first event
    if (isApprovedProvider && publishedEventsCount === 0) {
        return (<Card sx={{ mb: 3, border: '2px solid', borderColor: 'info.main' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <CelebrationIcon color="info" sx={{ fontSize: 40, mt: 0.5 }}/>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'info.main' }}>
                You&apos;re approved — publish your first training event
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your provider application for <strong>{agencyName}</strong> is approved. Create and publish your
                first event to start accepting registrations.
              </Typography>
              <Button component={Link} to="/dashboard?role=coordinator&tab=events" variant="contained" size="small">
                Create your first event
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>);
    }
    // Show approved status with published events (dismissible)
    if (isApprovedProvider && publishedEventsCount > 0) {
        if (!shouldShow) {
            return null;
        }
        return (<Card sx={{ mb: 3, border: '2px solid', borderColor: 'success.main', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', position: 'relative' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 40 }}/>
              <CelebrationIcon color="success" sx={{ fontSize: 32 }}/>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'success.main' }}>
                Congratulations! Your provider application has been approved.
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You now have full access to create and manage training events for <strong>{agencyName}</strong>.
              </Typography>
              
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Approved:</strong> {reviewedAt ? new Date(reviewedAt).toLocaleDateString() : 'Recently'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> <Chip label="Active Provider" color="success" size="small"/>
                </Typography>
              </Stack>

              <Divider sx={{ my: 2 }}/>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>What you can do now:</strong>
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  • Create and manage training events
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Set up event allocations for agencies, counties, and states
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Manage participant rosters and attendance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Access all platform features and tools
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
        
        {/* Close Button */}
        <IconButton onClick={handleDismiss} sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary',
                '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
            }} size="small" aria-label="Dismiss approval notification">
          <CloseIcon />
        </IconButton>
      </Card>);
    }
    // Show rejected status
    if (isRejectedProvider) {
        return (<Card sx={{ mb: 3, border: '2px solid', borderColor: 'error.main' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <CancelIcon color="error" sx={{ fontSize: 40, mt: 1 }}/>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
                Application Requires Additional Information
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your provider application for <strong>{agencyName}</strong> requires additional information before it can be approved.
              </Typography>
              
              {rejectionReason && (<Box sx={{ mb: 2, p: 2, bgcolor: 'error.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                    Reason for additional information needed:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {rejectionReason}
                  </Typography>
                </Box>)}

              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Submitted:</strong> {submittedAt ? new Date(submittedAt).toLocaleDateString() : 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Reviewed:</strong> {reviewedAt ? new Date(reviewedAt).toLocaleDateString() : 'Unknown'}
                </Typography>
              </Stack>

              <Divider sx={{ my: 2 }}/>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Next steps:</strong>
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  • Review the feedback above and gather the required information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Contact our support team if you need clarification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Submit a new application with the additional information
                </Typography>
              </Stack>

              <Box sx={{ mt: 3 }}>
                <Button component={Link} to="/provider-signup" variant="contained" color="primary" sx={{ mr: 2 }}>
                  Submit New Application
                </Button>
                <Button component={Link} to="/contact" variant="outlined" color="primary">
                  Contact Support
                </Button>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>);
    }
    // No application found - show call to action
    return (<Card sx={{ mb: 3, border: '2px solid', borderColor: 'info.main' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <InfoIcon color="info" sx={{ fontSize: 40, mt: 1 }}/>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'info.main' }}>
              Become a Training Provider
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You're logged in as a provider but don't have an active application. 
              Submit an application to start creating and managing training events.
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Button component={Link} to="/provider-signup" variant="contained" color="primary" startIcon={<BusinessIcon />}>
                Submit Provider Application
              </Button>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>);
};
export default ProviderAccessBanner;
